// Single source of truth for the dashboard's primary navigation.
//
// The shell renders this as a LEFT sidebar of top-level sections (one
// row per section, in <Sidebar>) plus a horizontal sub-tab bar of the
// active section's items (<SectionTabs>). Both derive everything from the
// current URL via findActiveNav — no extra state. Add a new page by
// dropping it into the relevant section here; this is the only place the
// nav structure is defined.
//
// React-free + pure so it can be unit-tested without rendering. Icons are
// attached in <Sidebar> (keyed by section.key) to keep this file free of
// React/lucide imports.

export interface NavItem {
  to: string;
  label: string;
}

export interface NavSection {
  key: string;
  label: string;
  items: NavItem[];
  // When set, the section is only rendered for users holding at least one
  // of these permissions. Evaluated by the renderer (<Sidebar>) via `can`
  // so this config stays React/permission-free. Absent ⇒ always shown.
  requiresAnyPermission?: string[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'overview',
    label: 'Overview',
    items: [{ to: '/app', label: 'Dashboard' }],
  },
  {
    key: 'projects',
    label: 'Projects',
    items: [{ to: '/projects', label: 'Projects' }],
  },
  {
    key: 'casework',
    label: 'Casework',
    items: [
      { to: '/evidence', label: 'Evidence' },
      { to: '/time-entries', label: 'Time' },
      { to: '/calendar', label: 'Calendar' },
    ],
  },
  {
    key: 'financials',
    label: 'Financials',
    items: [
      { to: '/invoices', label: 'Invoices' },
      { to: '/expenses', label: 'Expenses' },
      { to: '/contractor-payments', label: 'Pay' },
      { to: '/reports', label: 'Reports' },
    ],
  },
  {
    key: 'directory',
    label: 'Directory',
    items: [
      { to: '/directory', label: 'Directory' },
      { to: '/clients', label: 'Clients' },
      { to: '/contractors', label: 'Contractors' },
      { to: '/vendors', label: 'Vendors' },
      { to: '/staff', label: 'Staff' },
    ],
  },
  {
    key: 'resources',
    label: 'Resources',
    items: [
      { to: '/assets', label: 'Assets' },
      { to: '/service-rates', label: 'Rates' },
      { to: '/templates', label: 'Templates' },
      { to: '/documents', label: 'Documents' },
      { to: '/expiring', label: 'Expiring' },
    ],
  },
  {
    // Application/system settings hub. Admin-only: shown to users with at
    // least one of the settings/audit permissions (its tabs are otherwise
    // empty for them). Personal per-user settings live off-nav at
    // /settings (the topbar gear/username).
    key: 'settings',
    label: 'Settings',
    requiresAnyPermission: [
      'settings.read',
      'settings.write_company',
      'settings.write_security',
      'settings.manage_users',
      'settings.manage_rbac',
      'audit.read',
    ],
    items: [{ to: '/system-settings', label: 'Settings' }],
  },
];

export interface NavMatch {
  section: NavSection;
  item: NavItem;
}

// Resolve which section + item the current path belongs to. An item
// matches when the path equals it or is nested under it (`/evidence`
// matches `/evidence/new` and `/evidence/123`). The `${to}/` boundary
// guard prevents a prefix-only collision (`/evidence` must NOT match
// `/evidence-archive`). When several items match, the LONGEST `to` wins
// (longest-prefix), so more specific routes take precedence. Returns null
// for paths not in the nav (e.g. /settings, /audit) — callers treat that
// as "no section active".
export function findActiveNav(
  pathname: string,
  sections: NavSection[] = NAV_SECTIONS,
): NavMatch | null {
  let best: NavMatch | null = null;
  let bestLen = -1;
  for (const section of sections) {
    for (const item of section.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && item.to.length > bestLen) {
        best = { section, item };
        bestLen = item.to.length;
      }
    }
  }
  return best;
}
